import { useEffect, useRef, useState } from 'react';
import { useVideoPlayer, type VideoPlayer } from 'expo-video';

const AUDIO_3 = require('@/assets/audio/countdown/Carrie_Countdown_3.mp3');
const AUDIO_2 = require('@/assets/audio/countdown/Carrie_Countdown_2.mp3');
const AUDIO_1 = require('@/assets/audio/countdown/Carrie_Countdown_1.mp3');
const AUDIO_GO = require('@/assets/audio/Lets_Go.mp3');

export type CountdownStep = 3 | 2 | 1 | 'go' | 'done';

const STEP_SEQUENCE: { step: CountdownStep; audio: any }[] = [
  { step: 3, audio: AUDIO_3 },
  { step: 2, audio: AUDIO_2 },
  { step: 1, audio: AUDIO_1 },
  { step: 'go', audio: AUDIO_GO },
];

/**
 * Drives a 3-2-1-Go countdown using expo-video for audio playback.
 * Returns the current step and whether the countdown is active.
 * Calls `onComplete` when the sequence finishes.
 */
export function useCountdown(onComplete: () => void): {
  step: CountdownStep;
  isActive: boolean;
  player: VideoPlayer;
} {
  const [stepIndex, setStepIndex] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Latches once the sequence finishes. Guards against Android/ExoPlayer
  // re-emitting readyToPlay/playToEnd after the final "Let's Go" clip, which
  // would otherwise fire onComplete a second time.
  const completedRef = useRef(false);

  const player = useVideoPlayer(STEP_SEQUENCE[0].audio, (p) => {
    p.loop = false;
    p.muted = false;
    p.volume = 1.0;
  });

  // Auto-play when ready
  useEffect(() => {
    const sub = player.addListener('statusChange', (payload) => {
      if (completedRef.current) return;
      if (payload.status === 'readyToPlay') {
        player.play();
      }
    });
    return () => sub.remove();
  }, [player]);

  // Advance on each playToEnd
  useEffect(() => {
    const sub = player.addListener('playToEnd', () => {
      if (completedRef.current) return;
      const nextIndex = stepIndex + 1;
      if (nextIndex < STEP_SEQUENCE.length) {
        setStepIndex(nextIndex);
        player.replaceAsync(STEP_SEQUENCE[nextIndex].audio).catch((err) => {
          console.warn('[useCountdown] replaceAsync failed', err);
        });
      } else {
        completedRef.current = true;
        setStepIndex(nextIndex);
        onCompleteRef.current();
      }
    });
    return () => sub.remove();
  }, [player, stepIndex]);

  const step: CountdownStep =
    stepIndex < STEP_SEQUENCE.length ? STEP_SEQUENCE[stepIndex].step : 'done';

  return {
    step,
    isActive: step !== 'done',
    player,
  };
}
